import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IRequestAccess, PAYMENT_MODE } from 'src/app/@core/models/model';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { SubscriptionStateService } from 'src/app/@core/services/subscription-state.service';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { InputComponent } from 'src/app/@core/components/ui/input/input.component';
import { DataTableComponent } from 'src/app/@shared/components/data-table/data-table.component';
import { UserAvatarPipe } from 'src/app/@shared/pipes/user-avatar-pipe';
import { TableColumn, TableConfig } from 'src/app/@shared/models/table.models';
import { CreditPricing, UserCreditsService } from '../../services/user-credits.service';
import { CHECKOUT_SESSION_TYPE, CreditsTopupPayload, PaymentService } from 'src/app/contract-management/services/payment.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { AvatarStackComponent } from 'src/app/@shared/components/avatar-stack/avatar-stack.component';
import { IUser } from 'src/app/@core/models/model';
import { SearchBarComponent } from 'src/app/@shared/components/search-bar/search-bar.component';

export interface CheckoutItem {
    requestId: string;
    request: IRequestAccess;
    askCredits: number;
    agentCredits: number;
}

@Component({
    selector: 'app-credits-checkout-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogComponent, ButtonComponent, InputComponent, DataTableComponent, UserAvatarPipe, AvatarStackComponent, SearchBarComponent],
    templateUrl: './credits-checkout-dialog.component.html',
    styleUrl: './credits-checkout-dialog.component.scss',
})
export class CreditsCheckoutDialogComponent implements OnChanges {
    @Input() checkoutItems: CheckoutItem[] = [];
    @Input() visible: boolean = false;
    @Input() successUrl: string = '';
    @Input() cancelUrl: string = '';
    @Input() source: CHECKOUT_SESSION_TYPE = CHECKOUT_SESSION_TYPE.ACCEPT_REQUEST;
    @Output() visibleChange = new EventEmitter<boolean>();

    @ViewChild('checkoutUserTemplateRef') checkoutUserTemplateRef!: TemplateRef<any>;
    @ViewChild('checkoutAskBalanceTemplateRef') checkoutAskBalanceTemplateRef!: TemplateRef<any>;
    @ViewChild('checkoutAgentBalanceTemplateRef') checkoutAgentBalanceTemplateRef!: TemplateRef<any>;
    @ViewChild('checkoutAskTemplateRef') checkoutAskTemplateRef!: TemplateRef<any>;
    @ViewChild('checkoutAgentTemplateRef') checkoutAgentTemplateRef!: TemplateRef<any>;

    readonly MAX_TOTAL = 999999.99;

    searchQuery = '';

    get filteredCheckoutItems(): CheckoutItem[] {
        const q = this.searchQuery.trim().toLowerCase();
        if (!q) return this.checkoutItems;
        return this.checkoutItems.filter(item =>
            item.request.name?.toLowerCase().includes(q) ||
            item.request.email?.toLowerCase().includes(q)
        );
    }

    userBalances = new Map<string, { ask: number; agent: number }>();

    get checkoutUsers(): Partial<IUser>[] {
        return this.checkoutItems
            .map(item => item.request.requestedBy)
            .filter((u): u is IUser => typeof u === 'object' && u !== null);
    }

    getUserId(item: CheckoutItem): string {
        const rb = item.request.requestedBy;
        return typeof rb === 'object' && rb !== null ? (rb as IUser)._id : rb as string;
    }

    creditPricing: CreditPricing = { askCreditRatio: 1, agentCreditRatio: 1 };

    checkoutTableColumns: TableColumn[] = [
        { field: 'user', header: 'User', type: 'custom', template: 'checkoutUserTemplate' },
        { field: 'askBalance', header: 'Ask Balance', type: 'custom', template: 'checkoutAskBalanceTemplate' },
        { field: 'agentBalance', header: 'Agent Balance', type: 'custom', template: 'checkoutAgentBalanceTemplate' },
        { field: 'askCredits', header: 'Ask Credits', type: 'custom', template: 'checkoutAskTemplate' },
        { field: 'agentCredits', header: 'Agent Credits', type: 'custom', template: 'checkoutAgentTemplate' },
    ];

    checkoutTableConfig: TableConfig = {
        paginator: true,
        rows: 5,
        rowsPerPageOptions: [5, 10, 20],
        loading: false,
        selectable: false,
        emptyMessage: 'No users selected'
    };

    get checkoutTemplatesMap(): { [key: string]: TemplateRef<any> } {
        return {
            'checkoutUserTemplate': this.checkoutUserTemplateRef,
            'checkoutAskBalanceTemplate': this.checkoutAskBalanceTemplateRef,
            'checkoutAgentBalanceTemplate': this.checkoutAgentBalanceTemplateRef,
            'checkoutAskTemplate': this.checkoutAskTemplateRef,
            'checkoutAgentTemplate': this.checkoutAgentTemplateRef,
        };
    }

    get isSalesMode(): boolean {
        return this.subscriptionState.orgSubscriptionDetails()?.paymentMode === PAYMENT_MODE.SALES;
    }

    constructor(
        private userCreditsService: UserCreditsService,
        private paymentService: PaymentService,
        private toast: ToastService,
        private subscriptionState: SubscriptionStateService
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['visible']?.currentValue === true) {
            this.loadPricing();
            this.loadBalances();
        }
    }

    loadBalances() {
        this.userBalances.clear();
        this.checkoutItems.forEach(item => {
            const userId = this.getUserId(item);
            if (!userId) return;
            this.userCreditsService.getCreditsByUser(userId).subscribe({
                next: (res) => {
                    const data = res?.data || res;
                    this.userBalances.set(userId, {
                        ask: data?.ask?.remaining ?? 0,
                        agent: data?.agent?.remaining ?? 0,
                    });
                }
            });
        });
    }

    private loadPricing() {
        this.userCreditsService.getCurrentPricing().subscribe({
            next: (res: any) => {
                const data = res?.data || res;
                this.creditPricing = {
                    askCreditRatio: data?.askCreditRatio ?? 1,
                    agentCreditRatio: data?.agentCreditRatio ?? 1
                };
            }
        });
    }

    getItemTotal(item: CheckoutItem): number {
        const askCost = this.creditPricing.askCreditRatio > 0 ? item.askCredits / this.creditPricing.askCreditRatio : 0;
        const agentCost = this.creditPricing.agentCreditRatio > 0 ? item.agentCredits / this.creditPricing.agentCreditRatio : 0;
        return +(askCost + agentCost).toFixed(2);
    }

    get checkoutGrandTotal(): number {
        return +this.checkoutItems.reduce((sum, item) => sum + this.getItemTotal(item), 0).toFixed(2);
    }

    getTotalCredits(): number {
        return this.checkoutItems.reduce((sum, item) => sum + item.askCredits + item.agentCredits, 0);
    }

    increment(item: CheckoutItem, field: 'askCredits' | 'agentCredits') {
        item[field]++;
        if (this.checkoutGrandTotal > this.MAX_TOTAL) {
            item[field]--;
        }
    }

    decrement(item: CheckoutItem, field: 'askCredits' | 'agentCredits') {
        item[field] = Math.max(0, item[field] - 1);
    }

    onCreditsChange(item: CheckoutItem, field: 'askCredits' | 'agentCredits', value: number) {
        const clamped = (!value || isNaN(value) || value < 0) ? 0 : Math.floor(value);
        item[field] = clamped;

        if (this.checkoutGrandTotal <= this.MAX_TOTAL) return;

        const otherItemsTotal = this.checkoutItems
            .filter(i => i !== item)
            .reduce((sum, i) => sum + this.getItemTotal(i), 0);

        const ratio = field === 'askCredits'
            ? this.creditPricing.askCreditRatio
            : this.creditPricing.agentCreditRatio;

        const otherFieldCost = field === 'askCredits'
            ? (this.creditPricing.agentCreditRatio > 0 ? item.agentCredits / this.creditPricing.agentCreditRatio : 0)
            : (this.creditPricing.askCreditRatio > 0 ? item.askCredits / this.creditPricing.askCreditRatio : 0);

        const budgetForField = this.MAX_TOTAL - otherItemsTotal - otherFieldCost;
        item[field] = Math.min(clamped, Math.floor(Math.max(0, budgetForField * ratio)));
    }

    confirmCheckout() {
        const payload: CreditsTopupPayload = {
            successUrl: this.successUrl,
            cancelUrl: this.cancelUrl,
            users: this.checkoutItems.map(item => ({
                requestId: item.requestId,
                user: typeof item.request.requestedBy === 'object' && item.request.requestedBy !== null
                    ? (item.request.requestedBy as any)._id
                    : item.request.requestedBy,
                askCredits: item.askCredits,
                agentCredits: item.agentCredits,
            })),
            source: this.source
        };

        this.paymentService.createCheckoutSessionCreditsTopup(payload).subscribe({
            next: (res) => {
                const url = (res as any)?.data?.checkoutUrl;
                if (url) {
                    window.location.href = url;
                } else {
                    this.toast.success('Credits allocated successfully');
                    this.close();
                }
            },
            error: (err) => {
                this.toast.error(err?.error?.message || 'Failed to initiate payment');
            }
        });
    }

    close() {
        this.visibleChange.emit(false);
    }
}
